"use client";

/**
 * ToolHeader - Header component for tool displays
 *
 * Shows tool title, status badge, progress indicator, and expand/collapse control.
 */

import type { ReactNode } from "react";
import {
  Build,
  ExpandMore,
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
  Circle,
  Sync,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { colors, spacing, radius, textStyles } from "@/ui/tokens";
import type { ToolStatus } from "@/lib/sola/types/tools";
import { getToolDisplayName } from "@/lib/sola/constants/toolDisplayNames";
import { IconButton } from "./IconButton";
import headerStyles from "./ToolHeader.module.css";

export interface ToolHeaderProps {
  title: string;
  toolName: string;
  status: ToolStatus;
  progress?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  icon?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

/**
 * Human-readable status labels
 */
const STATUS_LABELS: Record<ToolStatus, string> = {
  idle: "Idle",
  "input-streaming": "Preparing",
  "input-ready": "Ready",
  executing: "Running",
  streaming: "Loading",
  complete: "Complete",
  error: "Error",
};

/**
 * Status icon component
 */
function StatusIcon({ status }: { status: ToolStatus }) {
  switch (status) {
    case "complete":
      return <CheckCircle style={{ fontSize: 16, color: colors.signal.success }} />;
    case "error":
      return <ErrorIcon style={{ fontSize: 16, color: colors.signal.danger }} />;
    case "executing":
    case "streaming":
      return (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Sync style={{ fontSize: 16, color: colors.signal.warning }} />
        </motion.div>
      );
    case "input-streaming":
    case "input-ready":
      return (
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <AccessTime style={{ fontSize: 16, color: colors.action.product }} />
        </motion.div>
      );
    default:
      return <Circle style={{ fontSize: 16, color: colors.text.muted }} />;
  }
}

/**
 * Get status badge background color
 */
function getStatusBadgeBg(status: ToolStatus): string {
  switch (status) {
    case "complete":
      return colors.signal.successLight;
    case "error":
      return colors.signal.dangerLight;
    case "executing":
    case "streaming":
      return colors.signal.warningLight;
    default:
      return colors.action.productLight;
  }
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: ToolStatus }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing[1],
        paddingLeft: spacing[2],
        paddingRight: spacing[2],
        paddingTop: spacing[1],
        paddingBottom: spacing[1],
        borderRadius: radius.sm,
        backgroundColor: getStatusBadgeBg(status),
      }}
    >
      <StatusIcon status={status} />
      <span
        style={{
          ...textStyles.caption,
          fontWeight: 500,
        }}
      >
        {STATUS_LABELS[status]}
      </span>
    </div>
  );
}

export function ToolHeader({
  title,
  toolName,
  status,
  progress,
  isExpanded = true,
  onToggleExpand,
  icon,
  actions,
  className,
}: ToolHeaderProps) {
  const displayTitle = title || getToolDisplayName(toolName);

  return (
    <div
      onClick={onToggleExpand}
      className={cn(
        headerStyles.header,
        onToggleExpand && headerStyles.clickable,
        className
      )}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: spacing[4],
        paddingLeft: spacing[4],
        paddingRight: spacing[4],
        paddingTop: spacing[3],
        paddingBottom: spacing[3],
        cursor: onToggleExpand ? "pointer" : "default",
        backgroundColor: colors.action.productLight,
        color: colors.action.product,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: spacing[2],
          minWidth: 0,
          flex: 1,
        }}
      >
        {icon || <Build style={{ fontSize: 18, flexShrink: 0 }} />}
        <span
          style={{
            ...textStyles.body2,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayTitle}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flexShrink: 0 }}>
        {actions ? (
          <div
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            style={{ display: "inline-flex", alignItems: "center", gap: spacing[1] }}
          >
            {actions}
          </div>
        ) : null}
        <StatusBadge status={status} />

        {onToggleExpand && (
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <IconButton color="inherit" style={{ color: "inherit" }}>
              <ExpandMore style={{ fontSize: 18 }} />
            </IconButton>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default ToolHeader;
